// src/features/pages/admin/roles/AssignmentsTab.tsx
// Assign roles (system + custom) to people in the tenant, and unassign.
// User list source: auth-service /api/v1/users (hrApi.listUsers) — its `id` IS
// the auth account id, which is exactly the `userId` the /rbac/assignments API expects.
//
// Compensation/payroll authorization Phase 5a: an assignment also says WHERE the
// role applies (no company limit / all authorized companies / selected
// companies) and optionally WHEN it ends; each row shows those as chips and can
// be edited. "Effective access" opens the server-computed preview for the person.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Search, UserCircle2, Plus, X, Pencil, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatDate } from '@/lib/orgFormat';
import { listUsers, type AdminUser } from '@/lib/hrApi';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import type { LegalEntityLite } from '@/features/payroll/types';
import {
  assignRole,
  assignmentIdFor,
  getAssignments,
  unassignRole,
  type Assignment,
  type RolesResponse,
  type Role,
} from '@/services/rbacService';
import { AssignmentScopeFields } from './AssignmentScopeFields';
import { EditAssignmentScopeDialog } from './EditAssignmentScopeDialog';
import { EffectiveAccessSheet } from './EffectiveAccessSheet';
import { EntityScopeReadinessPanel } from './EntityScopeReadinessPanel';
import { AllCompaniesAccessPanel } from './AllCompaniesAccessPanel';
import { useSaveWithLockoutConfirm } from './lockoutConfirm';
import { useConfirmScopeImpact } from './scopeImpactConfirm';
import {
  EMPTY_SCOPE_FORM,
  assignmentStatus,
  hasErrors,
  toCreatePayload,
  validateScopeForm,
  type ScopeForm,
} from './assignmentScope';

interface Props {
  roles: RolesResponse;
}

const displayName = (u: AdminUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;
const initials = (u: AdminUser) =>
  displayName(u)
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

export const AssignmentsTab: React.FC<Props> = ({ roles }) => {
  const confirm = useConfirm();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminUser | null>(null);

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  // A failed load is NOT "no roles assigned" — kept distinct so it never reads as all clear.
  const [assignmentsError, setAssignmentsError] = useState<{ variant: 'error' | 'denied'; message: string } | null>(null);
  const [addRoleId, setAddRoleId] = useState('');
  const [scopeForm, setScopeForm] = useState<ScopeForm>(EMPTY_SCOPE_FORM);
  const [triedAdd, setTriedAdd] = useState(false);
  const [busy, setBusy] = useState(false);

  // The tenant's companies, for the "Where" picker and to name entity chips.
  const [entities, setEntities] = useState<LegalEntityLite[]>([]);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Assignment | null>(null);
  const [preview, setPreview] = useState<{ userId: string; name: string } | null>(null);
  // Phase 7: bumped after any assignment change so the readiness panel re-checks.
  const [readinessKey, setReadinessKey] = useState(0);
  const saveWithLockoutConfirm = useSaveWithLockoutConfirm();
  const confirmScopeImpact = useConfirmScopeImpact();

  const allRoles: Role[] = useMemo(
    () => [...roles.systemRoles, ...roles.customRoles],
    [roles],
  );
  const roleById = useMemo(() => new Map(allRoles.map((r) => [r.id, r])), [allRoles]);
  const roleByKey = useMemo(() => new Map(allRoles.map((r) => [r.key, r])), [allRoles]);

  useEffect(() => {
    setLoadingUsers(true);
    listUsers({ page: 1, limit: 200 })
      .then((res) => setUsers(Array.isArray(res?.users) ? res.users : []))
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load users')))
      .finally(() => setLoadingUsers(false));
    payrollApi
      .listLegalEntities()
      .then((list) => {
        setEntities(Array.isArray(list) ? list : []);
        setEntitiesError(null);
      })
      .catch((e) =>
        setEntitiesError(getApiErrorMessage(e, 'Could not load your companies, so specific companies can’t be chosen right now.')),
      );
  }, []);

  const entityName = useCallback(
    (id: string) => entities.find((e) => e.id === id)?.name ?? `Company ${id.slice(0, 8)}`,
    [entities],
  );

  const loadAssignments = async (userId: string) => {
    setLoadingAssignments(true);
    setAssignmentsError(null);
    try {
      setAssignments(await getAssignments(userId));
    } catch (e) {
      const message = getApiErrorMessage(e, 'Could not load this person’s roles');
      toast.error(getApiErrorMessage(e, 'Could not load this person’s roles'));
      setAssignments([]);
      setAssignmentsError({ variant: errorVariant(e), message });
    } finally {
      setLoadingAssignments(false);
    }
  };

  const pickUser = (u: AdminUser) => {
    setSelected(u);
    setAddRoleId('');
    setScopeForm(EMPTY_SCOPE_FORM);
    setTriedAdd(false);
    void loadAssignments(u.id);
  };

  const scopeErrors = useMemo(
    () => validateScopeForm(scopeForm, undefined, entities.map((e) => e.id)),
    [scopeForm, entities],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => displayName(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [users, search]);

  // Roles not already assigned to the selected user (best-effort de-dupe by id/key).
  const assignedIds = useMemo(() => {
    const s = new Set<string>();
    assignments.forEach((a) => {
      if (a.roleId) s.add(a.roleId);
      if (a.roleKey) {
        const r = roleByKey.get(a.roleKey);
        if (r) s.add(r.id);
      }
    });
    return s;
  }, [assignments, roleByKey]);

  const availableRoles = useMemo(
    () => allRoles.filter((r) => !assignedIds.has(r.id)),
    [allRoles, assignedIds],
  );

  const labelForAssignment = (a: Assignment): { label: string; scope?: string } => {
    const r = (a.roleId && roleById.get(a.roleId)) || (a.roleKey && roleByKey.get(a.roleKey)) || null;
    return { label: a.roleLabel || r?.label || a.roleKey || a.roleId || 'Role', scope: a.scope || r?.scope };
  };

  const add = async () => {
    if (!selected || !addRoleId) return;
    setTriedAdd(true);
    if (hasErrors(scopeErrors)) return;
    setBusy(true);
    try {
      const payload = { userId: selected.id, roleId: addRoleId, ...toCreatePayload(scopeForm, entities.map((e) => e.id)) };
      // A scoped role can supersede broader access the person has today (e.g.
      // the same role unscoped on their profile): ask before they lose companies.
      // An unscoped add can only add reach, so it never asks.
      if (payload.entityScope) {
        const go = await confirmScopeImpact({
          assignmentId: assignmentIdFor(selected.id, addRoleId),
          scope: payload,
          personName: displayName(selected),
          roleLabel: roleById.get(addRoleId)?.label ?? 'this role',
          entityName,
        });
        if (!go) return;
      }
      // The first company-scoped role can lock people out of payroll: the
      // server names them and we ask before resending with the acknowledgement.
      const result = await saveWithLockoutConfirm((ack) =>
        assignRole(ack ? { ...payload, acknowledgeLockout: true } : payload),
      );
      if (!result.saved) return;
      setReadinessKey((k) => k + 1);
      const r = roleById.get(addRoleId);
      toast.success(`Assigned “${r?.label ?? 'role'}” to ${displayName(selected)}`);
      setAddRoleId('');
      setScopeForm(EMPTY_SCOPE_FORM);
      setTriedAdd(false);
      await loadAssignments(selected.id);
    } catch (e) {
      // 403 "cannot grant a permission you do not hold" etc. surface verbatim.
      toast.error(getApiErrorMessage(e, 'Could not assign the role'), { duration: 7000 });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a: Assignment) => {
    if (!selected) return;
    const { label } = labelForAssignment(a);
    const ok = await confirm({
      title: 'Remove role',
      message: `Remove “${label}” from ${displayName(selected)}?`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await unassignRole(a.id);
      setReadinessKey((k) => k + 1);
      toast.success(`Removed “${label}” from ${displayName(selected)}`);
      await loadAssignments(selected.id);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not remove the role'), { duration: 7000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <EntityScopeReadinessPanel
        reloadKey={readinessKey}
        onOpenAccess={(userId, name) => setPreview({ userId, name })}
      />
      <AllCompaniesAccessPanel
        reloadKey={readinessKey}
        nameOf={(id) => {
          const u = users.find((x) => x.id === id);
          return u ? displayName(u) : '';
        }}
      />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,340px)_1fr] gap-6">
        {/* User picker */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-cream border border-border mb-3">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search people…"
                className="bg-transparent outline-none text-sm w-full text-foreground"
              />
            </div>
            {loadingUsers ? (
              <div className="py-10 flex items-center justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">No people found.</div>
            ) : (
              <ul className="max-h-[56vh] overflow-y-auto -mx-2">
                {filtered.map((u) => {
                  const active = selected?.id === u.id;
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => pickUser(u)}
                        className={`w-full text-left flex items-center gap-3 px-2 py-2 rounded-[10px] transition-colors ${
                          active ? 'bg-orange-50 dark:bg-orange-500/10' : 'hover:bg-muted/40'
                        }`}
                      >
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 font-semibold text-xs shrink-0">
                          {initials(u)}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground truncate">
                            {displayName(u)}
                          </span>
                          <span className="block text-xs text-muted-foreground truncate">{u.email}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Selected user's roles */}
        <Card>
          <CardContent className="pt-6">
            {!selected ? (
              <div className="py-16 text-center text-muted-foreground">
                <UserCircle2 className="w-8 h-8 mx-auto mb-2 opacity-60" />
                Select a person to view and manage their roles.
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="text-h3 text-foreground">{displayName(selected)}</h3>
                    <p className="text-sm text-muted-foreground">{selected.email}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreview({ userId: selected.id, name: displayName(selected) })}
                  >
                    <ShieldCheck className="w-4 h-4 mr-1" />
                    Effective access
                  </Button>
                </div>

                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm font-medium text-foreground" htmlFor="add-role">
                      Add a role
                    </label>
                    <select
                      id="add-role"
                      value={addRoleId}
                      onChange={(e) => setAddRoleId(e.target.value)}
                      disabled={busy || availableRoles.length === 0}
                      className="mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400 disabled:opacity-60"
                    >
                      <option value="">
                        {availableRoles.length === 0 ? 'All roles assigned' : 'Choose a role…'}
                      </option>
                      {availableRoles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.label} {r.isSystem ? '(system)' : ''} · {r.scope}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button onClick={add} disabled={!addRoleId || busy}>
                    {busy ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-1" />
                    )}
                    Assign
                  </Button>
                </div>
                {addRoleId && (
                  <div className="rounded-[10px] border border-border p-3">
                    <AssignmentScopeFields
                      idPrefix="add-scope"
                      value={scopeForm}
                      onChange={setScopeForm}
                      errors={scopeErrors}
                      showErrors={triedAdd}
                      entities={entities}
                      entitiesError={entitiesError}
                      disabled={busy}
                    />
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">Assigned roles</h4>
                  {loadingAssignments ? (
                    <div className="py-8 flex items-center justify-center text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading roles…
                    </div>
                  ) : assignmentsError ? (
                    <DataState
                      compact
                      variant={assignmentsError.variant}
                      message={assignmentsError.message}
                      onRetry={() => void loadAssignments(selected.id)}
                    />
                  ) : assignments.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      No roles assigned yet.
                    </div>
                  ) : (
                    <ul className="divide-y divide-border">
                      {assignments.map((a) => {
                        const { label, scope } = labelForAssignment(a);
                        const status = assignmentStatus(a);
                        const ids = a.legalEntityIds ?? [];
                        return (
                          <li key={a.id} className="flex items-center gap-3 py-2.5" data-testid={`assignment-${a.id}`}>
                            <span className="flex-1 min-w-0">
                              <span
                                className={`block text-sm font-medium ${
                                  status === 'active' ? 'text-foreground' : 'text-muted-foreground line-through'
                                }`}
                              >
                                {label}
                              </span>
                              <span className="mt-1 flex flex-wrap gap-1">
                                {a.entityScope === 'ALL_AUTHORIZED_ENTITIES' && (
                                  <Badge variant="warning">All companies, incl. ones added later</Badge>
                                )}
                                {a.entityScope === 'SELECTED_ENTITIES' &&
                                  ids.slice(0, 3).map((id) => (
                                    <Badge key={id} variant="neutral">
                                      {entityName(id)}
                                    </Badge>
                                  ))}
                                {a.entityScope === 'SELECTED_ENTITIES' && ids.length > 3 && (
                                  <Badge variant="neutral" title={ids.slice(3).map(entityName).join(', ')}>
                                    +{ids.length - 3} more
                                  </Badge>
                                )}
                                {status === 'active' && a.expiresAt && (
                                  <Badge variant="info">Ends {formatDate(a.expiresAt)}</Badge>
                                )}
                                {status === 'expired' && <Badge variant="neutral">Expired</Badge>}
                                {status === 'revoked' && <Badge variant="neutral">Revoked</Badge>}
                              </span>
                            </span>
                            {scope && (
                              <Badge variant="neutral" className="capitalize">
                                {scope}
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground"
                              onClick={() => setEditing(a)}
                              disabled={busy}
                              aria-label={`Change where ${label} applies`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-danger"
                              onClick={() => remove(a)}
                              disabled={busy}
                              aria-label={`Remove ${label}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <EditAssignmentScopeDialog
          assignment={editing}
          roleLabel={editing ? labelForAssignment(editing).label : ''}
          personName={selected ? displayName(selected) : ''}
          entities={entities}
          entitiesError={entitiesError}
          entityName={entityName}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setReadinessKey((k) => k + 1);
            return selected ? loadAssignments(selected.id) : undefined;
          }}
        />
        <EffectiveAccessSheet
          userId={preview?.userId ?? null}
          personName={preview?.name ?? 'this person'}
          entityName={entityName}
          onClose={() => setPreview(null)}
        />
      </div>
    </div>
  );
};

export default AssignmentsTab;
